import { useMemo } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, Row } from "@tanstack/react-table";
import { type BusinessUserWithSeats } from "lens-platform-sdk/dist/cjs/BusinessService";
import { Lozenge } from "@k8slens/lds";

import { useTrackedTableSort } from "src/hooks/useTrackedTableSort";

import { getUserAvatar } from "src/utils/user";
import { getUserFullname } from "src/services/getUserFullname";

import Table from "src/components/Table/Table";
import { AvatarTableCell, PendingUserTableCell } from "src/components/TableCells/AvatarTableCell";
import DateCell, { dateColDef, dateColSortingFn } from "src/components/TableCells/DateCell";

const tableColumnHelper = createColumnHelper<BusinessUserWithSeats>();

interface Props {
  businessUsers: Array<BusinessUserWithSeats>;
  loading?: boolean;
  notDataText: string;
  caption: string;
}

const isOfflineSeat = (d: Row<BusinessUserWithSeats>) => {
  const { seats } = d.original;

  return seats.some((seat) => seat.active && seat.offline);
};

const Users = ({ businessUsers, loading, notDataText, caption }: Props) => {
  const tableColumns = [
    tableColumnHelper.accessor("username", {
      header: () => <span>Lens ID</span>,
      cell: ({ row, getValue }) => {
        const username = getValue();
        const user = row.original;
        const { email } = user;
        const userAvatar = getUserAvatar(user);
        const role = user.role === "Administrator" ? "Admin" : undefined;

        // User is invited to the business, but not yet have a Lens ID account
        const userNotHaveAccount = email && !username;

        if (userNotHaveAccount) {
          return <PendingUserTableCell email={email} />;
        }

        const fullname = getUserFullname(user);

        return <AvatarTableCell image={userAvatar} name={username ?? ""} subtitle={fullname} role={role} />;
      },
      meta: { primary: true },
    }),
    tableColumnHelper.accessor("lastAccess", {
      ...dateColDef,
      sortingFn: (row1, row2, key) => {
        const firstOffline = isOfflineSeat(row1);
        const secondOffline = isOfflineSeat(row2);

        if (!firstOffline && secondOffline) {
          return row1.original.lastAccess ? 1 : -1;
        } else if (firstOffline && !secondOffline) {
          return row2.original.lastAccess ? -1 : 1;
        }

        return dateColSortingFn(row1, row2, key);
      },
      cell: (props) => {
        if (isOfflineSeat(props.row)) {
          return (
            <div>
              <Lozenge textTransform="uppercase">Offline</Lozenge>
            </div>
          );
        }

        return <DateCell {...props} />;
      },
      header: () => <span>Last Seen</span>,
    }),
  ];
  const { sorting, onSortingChange } = useTrackedTableSort({
    table: caption,
    initialSort: [{ id: "username", desc: false }],
  });

  const table = useReactTable({
    data: businessUsers,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange,
  });
  const rowModel = table.getRowModel();
  const rows = useMemo(() => rowModel.rows, [rowModel]);

  return (
    <Table<BusinessUserWithSeats>
      caption={caption}
      loading={loading}
      rows={rows}
      columns={table.getAllColumns()}
      headerGroups={table.getHeaderGroups()}
      noDataText={notDataText}
    />
  );
};

export default Users;
